// `avatar status [--json]` — Command 06 spec.
// Read-only snapshot: project name, CLI version, pack version, pending count,
// backup count, tech-stack first-line. No mutations.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import boxen from "boxen";
import type { Command } from "commander";
import { discoverEnabledAndAvailableTools } from "../lib/discover-pack-tools-and-defaults.js";
import { pathExists, readText } from "../lib/filesystem-helpers.js";
import { listBackups } from "../lib/pack-backup-manager.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { readPinnedPackVersion } from "../lib/team-pack-submodule-manager.js";
import { chalk, log } from "../lib/terminal-logger.js";

export function registerStatusCommand(program: Command): void {
  program
    .command("status")
    .description("Snapshot tức thì: project, pack version, pending, backup")
    .option("--json", "Output JSON cho script")
    .action(async (opts: { json?: boolean }) => {
      try {
        const snapshot = await gatherStatus(process.cwd());
        if (opts.json) {
          process.stdout.write(`${JSON.stringify(snapshot, null, 2)}\n`);
        } else {
          renderStatusBox(snapshot);
        }
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

interface StatusSnapshot {
  projectName: string;
  cliVersion: string;
  packVersion: string | null;
  pendingCount: number;
  backupCount: number;
  techStackSummary: string;
  hasAvatar: boolean;
  toolsEnabled: string[];
  toolsAvailableCount: number;
}

async function gatherStatus(cwd: string): Promise<StatusSnapshot> {
  const projectName = cwd.split("/").filter(Boolean).pop() ?? "unknown";
  const claudeRoot = join(cwd, ".claude");
  const hasAvatar = await pathExists(claudeRoot);
  if (!hasAvatar) {
    return {
      projectName,
      cliVersion: readCliVersion(),
      packVersion: null,
      pendingCount: 0,
      backupCount: 0,
      techStackSummary: "(Avatar chưa init)",
      hasAvatar: false,
      toolsEnabled: [],
      toolsAvailableCount: 0,
    };
  }

  // v1.13.0 perf: 4 probes độc lập (pack, pending, backup, tech-stack) chạy
  // parallel qua Promise.all thay vì sequential. Save ~100-200ms trên repo
  // có nhiều file/backup. Mỗi probe self-catch (.catch → safe fallback) để
  // 1 probe fail không kill cả status command.
  const pendingDir = join(claudeRoot, "_pending");
  const [packVersion, pendingCount, backupCount, techStackSummary, tools] = await Promise.all([
    (async (): Promise<string | null> => {
      // v3: pack là tarball (không git) → đọc version từ manifest. null nếu chưa cài.
      if (!(await pathExists(join(claudeRoot, "pack")))) return null;
      return readPinnedPackVersion(cwd).catch(() => null);
    })(),
    (async (): Promise<number> => {
      if (!(await pathExists(pendingDir))) return 0;
      const entries = await fs.readdir(pendingDir);
      return entries.filter((n) => n.endsWith(".diff.md")).length;
    })(),
    listBackups(cwd)
      .then((b) => b.length)
      .catch(() => 0),
    readTechStackFirstLine(claudeRoot).catch(() => "(read error)"),
    discoverEnabledAndAvailableTools(cwd).catch(() => ({ available: [], enabled: [] })),
  ]);

  return {
    projectName,
    cliVersion: readCliVersion(),
    packVersion,
    pendingCount,
    backupCount,
    techStackSummary,
    hasAvatar: true,
    toolsEnabled: tools.enabled,
    toolsAvailableCount: tools.available.length,
  };
}

async function readTechStackFirstLine(claudeRoot: string): Promise<string> {
  const techStackPath = join(claudeRoot, "project", "tech-stack.md");
  if (!(await pathExists(techStackPath))) return "(no tech-stack.md)";
  const content = await readText(techStackPath);
  const firstNonHeaderLine = content
    .split("\n")
    .find((l) => l.trim() && !l.startsWith("#") && !l.startsWith(">"));
  return firstNonHeaderLine?.trim() ?? "(empty)";
}

// "prompt-scoring (1/1 available)" hoặc "none (1 available)" / "none".
function formatTools(s: StatusSnapshot): string {
  if (s.toolsEnabled.length > 0) {
    return `${s.toolsEnabled.join(", ")} (${s.toolsEnabled.length}/${s.toolsAvailableCount} available)`;
  }
  if (s.toolsAvailableCount > 0) {
    return `none enabled (${s.toolsAvailableCount} available — avatar tool list)`;
  }
  return "none";
}

function renderStatusBox(s: StatusSnapshot): void {
  const lines = [
    `${chalk.bold("Avatar Status")} · ${chalk.cyan(s.projectName)}`,
    "─".repeat(48),
    `${chalk.dim("CLI version:")}        ${s.cliVersion}`,
    `${chalk.dim("Pack version:")}       ${s.packVersion ?? chalk.yellow("not installed")}`,
    `${chalk.dim("Pending changes:")}    ${s.pendingCount}${s.pendingCount > 0 ? chalk.dim(" (avatar review)") : ""}`,
    `${chalk.dim("Backups:")}            ${s.backupCount}`,
    `${chalk.dim("Tech stack:")}         ${s.techStackSummary}`,
    `${chalk.dim("Tools:")}           ${formatTools(s)}`,
  ];
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}
